import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Box, FormControlLabel, Switch, IconButton, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { forceCollide, forceCenter, forceRadial } from 'd3-force';

// ---------- Helpers ----------
const cleanId = (id) => id?.toLowerCase().replace(/[:\-]/g, '');

const ICON = {
  switchW: 60,
  switchH: 20,

  apW: 36,      // 👈 AP mindre & mer fyrkantig
  apH: 36,

  neighborW: 50,
  neighborH: 18,
};

const parseNeighbors = (neighbors) => {
  try {
    const parsed = typeof neighbors === 'string' ? JSON.parse(neighbors) : neighbors;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const switchIcon = (status) => {
  if (status === 'online') return `${process.env.PUBLIC_URL}/switch-online.png`;
  if (status === 'offline') return `${process.env.PUBLIC_URL}/switch-offline.png`;
  return `${process.env.PUBLIC_URL}/unidentified.png`;
};

const neighborIcon = (isAP) =>
  isAP ? `${process.env.PUBLIC_URL}/ap.png` : `${process.env.PUBLIC_URL}/unidentified.png`;

// Preload/cache images so canvas drawing is fast
const imgCache = new Map();
const getImage = (src) => {
  if (!src) return null;
  if (imgCache.has(src)) return imgCache.get(src);
  const img = new Image();
  img.src = src;
  imgCache.set(src, img);
  return img;
};

const NetworkGraph = ({ completeSwitches, setDialogData, highlightedNode }) => {
  const fgRef = useRef(null);
  const wrapRef = useRef(null);

  const [showAPs, setShowAPs] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ✅ Highlight state
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedLinkKey, setSelectedLinkKey] = useState(null);

  // ---------- Forces (THE knobs you can tweak) ----------
  const FORCES = {
    linkDistance: 80,       // ↑ more distance between connected nodes
    chargeStrength: -180,    // more negative = more repulsion (less clump)
    collisionRadius: 58,    // prevents overlap
    centerStrength: 0.06,   // pulls everything slightly toward center
    radialRadius: 900,      // keeps "islands" closer
    radialStrength: 0.02,   // how much to pull islands inward
  };

  // ---------- Build graph data ----------
  const graphData = useMemo(() => {
    const nodesById = new Map();
    const linksByKey = new Map();

    const known = new Set(
      completeSwitches.map((s) => cleanId(s?.chassis_id)).filter(Boolean)
    );

    const getLabel = (sid, fallback) => {
      const sw = completeSwitches.find((x) => cleanId(x?.chassis_id) === sid);
      return sw?.name || sw?.ip || fallback || sid;
    };

    const ensureNode = (node) => {
      if (!node?.id) return;
      if (!nodesById.has(node.id)) nodesById.set(node.id, node);
    };

    // Add switch nodes
    completeSwitches.forEach((sw) => {
      const id = cleanId(sw?.chassis_id);
      if (!id) return;

      ensureNode({
        id,
        type: 'switch',
        label: sw.name || sw.ip || id,
        name: sw.name,
        ip: sw.ip,
        location: sw.location, 
        version: sw.version,
        model: sw.model,
        status: sw.status,
        icon: switchIcon(sw.status),
      });
    });

    // Add neighbor nodes + links
    completeSwitches.forEach((sw) => {
      const sid = cleanId(sw?.chassis_id);
      if (!sid) return;

      const neighbors = parseNeighbors(sw.neighbors);

      Object.entries(neighbors).forEach(([type, list]) => {
        if (!Array.isArray(list)) return;

        list.forEach((n) => {
          const nid = cleanId(n?.chassis_id);
          if (!nid) return;

          const isAP = type === 'WLAN';
          if (isAP && !showAPs) return;

          // Neighbor node (only if not a known switch)
          if (!known.has(nid)) {
            ensureNode({
              id: nid,
              type: isAP ? 'ap' : 'neighbor',
              label: n?.chassis_name || nid,
              name: n?.chassis_name,
              ip: n?.mgmt_ip_list,
              model: n?.chassis_description,
              status: undefined,
              icon: neighborIcon(isAP),
            });
          }

          const isSwitchToSwitch = known.has(sid) && known.has(nid);

          if (isSwitchToSwitch) {
            // Dedupe A<->B into one link
            const [a, b] = sid < nid ? [sid, nid] : [nid, sid];
            const key = `${a}--${b}`;

            const fromThisSideIsA = sid === a;
            const portField = fromThisSideIsA ? 'aPort' : 'bPort';

            const existing = linksByKey.get(key);
            if (!existing) {
              linksByKey.set(key, {
                key,
                source: a,
                target: b,
                aName: getLabel(a),
                bName: getLabel(b),
                aPort: null,
                bPort: null,
                [portField]: n?.interface || null,
              });
            } else {
              existing[portField] = n?.interface || existing[portField];
              existing.aName = getLabel(existing.source, existing.aName);
              existing.bName = getLabel(existing.target, existing.bName);
            }
          } else {
            // Switch -> neighbor (keep as one link, avoid dup)
            const key = `${sid}--${nid}`;
            if (!linksByKey.has(key)) {
              linksByKey.set(key, {
                key,
                source: sid,
                target: nid,
                aName: getLabel(sid),
                bName: n?.chassis_name || nid,
                aPort: n?.interface || null,
                bPort: null,
              });
            }
          }
        });
      });
    });

    return {
      nodes: Array.from(nodesById.values()),
      links: Array.from(linksByKey.values()),
    };
  }, [completeSwitches, showAPs]);

  // ✅ Apply forces once per graph update (NOT per tick)
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;

    fg.d3Force('charge')?.strength(FORCES.chargeStrength);
    fg.d3Force('collide', forceCollide(FORCES.collisionRadius));
    fg.d3Force('center', forceCenter(0, 0).strength(FORCES.centerStrength));
    fg.d3Force('radial', forceRadial(FORCES.radialRadius, 0, 0).strength(FORCES.radialStrength));

    fg.d3ReheatSimulation?.();
  }, [graphData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Fullscreen ----------
  const toggleFullscreen = () => setIsFullscreen((v) => !v);

  useEffect(() => {
    const t = setTimeout(() => fgRef.current?.zoomToFit?.(300, 60), 100);
    return () => clearTimeout(t);
  }, [isFullscreen, showAPs]);

  // ---------- Center/highlight from table ----------
  useEffect(() => {
    if (!highlightedNode || !fgRef.current) return;
    const id = cleanId(highlightedNode);
    const node = graphData.nodes.find((n) => n.id === id);
    if (!node) return;

    setSelectedNodeId(id);
    setSelectedLinkKey(null);

    fgRef.current.centerAt(node.x ?? 0, node.y ?? 0, 600);
    fgRef.current.zoom(1.6, 600);
  }, [highlightedNode, graphData.nodes]);

  // ---------- Click handlers ----------
  const onNodeClick = useCallback(
    (node, event) => {
      const x = event?.clientX ?? 0;
      const y = event?.clientY ?? 0;

      // ✅ set highlight
      setSelectedNodeId(node.id);
      setSelectedLinkKey(null);

      setDialogData({
        type: node.type || 'switch',
        name: node.name || node.label || node.id,
        ip: node.ip,
        location: node.location,
        version: node.version,
        model: node.model,
        status: node.status,
        x,
        y,
      });
    },
    [setDialogData]
  );

  const onLinkClick = useCallback(
    (link, event) => {
      const x = event?.clientX ?? 0;
      const y = event?.clientY ?? 0;

      // ✅ set highlight
      setSelectedLinkKey(link.key);
      setSelectedNodeId(null);

      const parts = [];
      if (link?.aPort) parts.push(`${link.aName}: ${link.aPort}`);
      if (link?.bPort) parts.push(`${link.bName}: ${link.bPort}`);

      setDialogData({
        type: 'link',
        fromName: link?.aName || link?.source,
        toName: link?.bName || link?.target,
        fromPort: link?.aPort || null,
        toPort: link?.bPort || null,
        title: parts.join('\n'),
        x,
        y,
      });
    },
    [setDialogData]
  );

  const onBackgroundClick = useCallback(() => {
    setDialogData(null);

    // ✅ clear highlight
    setSelectedNodeId(null);
    setSelectedLinkKey(null);
  }, [setDialogData]);

  // ---------- Drawing ----------
  const drawNode = useCallback(
    (node, ctx, globalScale) => {
      const img = getImage(node.icon);

      let w, h;

      if (node.type === 'switch') {
        w = ICON.switchW;
        h = ICON.switchH;
      } else if (node.type === 'ap') {
        w = ICON.apW;
        h = ICON.apH;
      } else {
        w = ICON.neighborW;
        h = ICON.neighborH;
      }

      // ✅ HALO / MASK: breaks edges that pass behind nodes (so it doesn't look connected)
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(node.x, node.y, w / 2 + 6, h / 2 + 6, 0, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      // draw icon
      if (img && img.complete) {
        ctx.drawImage(img, node.x - w / 2, node.y - h / 2, w, h);
      } else {
        ctx.save();
        ctx.fillStyle = '#ddd';
        ctx.fillRect(node.x - w / 2, node.y - h / 2, w, h);
        ctx.restore();
      }

      // ✅ highlight node
      if (node.id === selectedNodeId) {
        ctx.save();
        ctx.strokeStyle = '#ff0000ff';
        ctx.lineWidth = 3 / globalScale;
        ctx.strokeRect(node.x - w / 2 - 4, node.y - h / 2 - 4, w + 8, h + 8);
        ctx.restore();
      }

      // labels
      const showLabel = globalScale > 0.75;
      if (showLabel) {
        const fontSize = Math.max(10, 12 / globalScale);
        ctx.font = `${fontSize}px Sans-Serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#111';

        const text = node.label || node.id;
        ctx.fillText(text, node.x, node.y + h / 2 + 3);
      }
    },
    [selectedNodeId]
  );

  const paintPointerArea = useCallback((node, color, ctx) => {
      let w, h;

      if (node.type === 'switch') {
        w = ICON.switchW + 10;
        h = ICON.switchH + 10;
      } else if (node.type === 'ap') {
        w = ICON.apW + 10;
        h = ICON.apH + 10;
      } else {
        w = ICON.neighborW + 10;
        h = ICON.neighborH + 10;
      }

    ctx.fillStyle = color;
    ctx.fillRect(node.x - w / 2, node.y - h / 2, w, h);
  }, []);

  return (
    <Box
      ref={wrapRef}
      sx={{
        position: isFullscreen ? 'fixed' : 'relative',
        inset: isFullscreen ? 0 : 'auto',
        height: isFullscreen ? '100vh' : '85vh',
        width: '100%',
        zIndex: isFullscreen ? 9999 : 'auto',
        bgcolor: '#fff',
      }}
    >
      {/* Controls */}
      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>
        <FormControlLabel
          control={<Switch checked={showAPs} onChange={() => setShowAPs((v) => !v)} />}
          label="Show Access Points"
        />
      </Box>

      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ mr: 1 }}>Full screen</Typography>
        <IconButton onClick={toggleFullscreen}>
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Box>

      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}

        // performance
        cooldownTicks={120}
        warmupTicks={80}
        enableNodeDrag
        d3VelocityDecay={0.25}

        // forces
        d3Force="charge"
        linkDistance={FORCES.linkDistance}

        // ✅ highlight link here
        linkWidth={(l) => (l.key === selectedLinkKey ? 3 : 1)}
        linkColor={(l) => (l.key === selectedLinkKey ? '#ff9800' : '#9aa0a6')}
        linkOpacity={(l) => (l.key === selectedLinkKey ? 0.95 : 0.55)}

        onEngineStop={() => fgRef.current?.zoomToFit?.(300, 80)}

        // controls
        onBackgroundClick={onBackgroundClick}
        onNodeClick={onNodeClick}
        onLinkClick={onLinkClick}

        // drawing
        nodeCanvasObject={drawNode}
        nodePointerAreaPaint={paintPointerArea}
      />
    </Box>
  );
};

export default NetworkGraph;